// src/common/components/serviceOtpContent/useServiceOtp.ts
import { useCallback, useEffect, useState } from "react";
import {
  saveToStorage,
  getFromStorage,
} from "../../utils/functions/localStorage";
import { getApi } from "../../../api/api";
import { useSelector } from "react-redux";
import { selectIsOnline } from "../../../store/slices/networkSlice";

interface ServiceOtpData {
  codes: string[];
  codesLeft: number;
}

const STORAGE_KEY = "service_otp";

export const useServiceOtp = () => {
  const isOnline = useSelector(selectIsOnline);
  const [otpData, setOtpData] = useState<ServiceOtpData | null>(() =>
    getFromStorage<ServiceOtpData>(STORAGE_KEY),
  );
  const [loading, setLoading] = useState(false);
  const [api] = useState(getApi);

  const refreshOtp = useCallback(async () => {
    if (!isOnline) return;
    setLoading(true);
    try {
      const data = await api.getServiceOtp();
      if (data) {
        saveToStorage(STORAGE_KEY, data);
        setOtpData(data);
      }
    } catch (error) {
      console.error("Failed to fetch service otp:", error);
    } finally {
      setLoading(false);
    }
  }, [isOnline, api]);

  const nextCode = useCallback(() => {
    if (!otpData || otpData.codes.length === 0) return;

    if (otpData.codes.length === 1) {
      if (isOnline) {
        refreshOtp();
      }
      return;
    }

    const [, ...remaining] = otpData.codes;
    const updated = { codes: remaining, codesLeft: remaining.length };
    saveToStorage(STORAGE_KEY, updated);
    setOtpData(updated);
  }, [otpData, refreshOtp, isOnline]);

  useEffect(() => {
    if (!otpData || otpData.codes.length === 0) {
      refreshOtp();
    }
  }, []);

  return { otpData, loading, nextCode, refreshOtp };
};
